import React, { useState } from 'react';
import Select from 'react-select';
import api from '../../../../../axiosInstance';
import { useApp } from '../../../../../AppProvider';
import { useAuth } from '../../../../../AuthProvider';
import './MedicalCertificateModal.css';

const CERT_TYPES = ['Sick Leave', 'Fitness', 'Medical Necessity', 'Disability', 'Other'];

const CERT_PREFILLS = {
  'Sick Leave': {
    purpose: 'Submission to employer for leave application',
    remarks: 'Patient requires complete bed rest and adequate hydration. Avoid strenuous activity and exposure to cold. Return for follow-up if symptoms persist beyond the recommended leave period.',
  },
  'Fitness': {
    purpose: 'Return-to-work clearance after medical leave',
    remarks: 'Patient has been clinically assessed and is medically fit to resume regular duties. No functional restrictions apply unless otherwise indicated. Follow up as scheduled.',
  },
  'Medical Necessity': {
    purpose: 'Insurance pre-authorisation for specialist referral and treatment',
    remarks: 'The recommended investigation, treatment, or specialist referral is medically necessary for the appropriate management of the patient\'s condition. This certificate is issued to support pre-authorisation or reimbursement claims.',
  },
  'Disability': {
    purpose: 'Disability assessment and documentation for official records',
    remarks: 'Patient has been examined and found to have a medical condition that significantly impacts functional capacity. Adequate rest, avoidance of physical strain, and appropriate supportive measures are advised. Periodic reassessment is recommended.',
  },
  'Other': {
    purpose: 'Medical certificate issued as per patient\'s request',
    remarks: 'Patient has been examined and this certificate is issued based on clinical findings at the time of consultation.',
  },
};

const DIAGNOSIS_OPTIONS = [
  'Viral Fever', 'Influenza (Flu)', 'COVID-19', 'Dengue Fever', 'Chikungunya', 'Malaria',
  'Typhoid Fever', 'Tuberculosis', 'Upper Respiratory Tract Infection',
  'Lower Respiratory Tract Infection', 'Acute Bronchitis', 'Pneumonia', 'Allergic Rhinitis',
  'Sinusitis', 'Pharyngitis', 'Otitis Media', 'Conjunctivitis', 'Urinary Tract Infection',
  'Acute Gastroenteritis', 'Helicobacter pylori Infection', 'Hypertension',
  'Coronary Artery Disease', 'Ischemic Heart Disease', 'Congestive Heart Failure',
  'Type 1 Diabetes Mellitus', 'Type 2 Diabetes Mellitus', 'Prediabetes', 'Dyslipidemia',
  'Metabolic Syndrome', 'Hypothyroidism', 'Hyperthyroidism', 'Obesity',
  'Polycystic Ovary Syndrome (PCOS)', 'Gastritis', 'Gastroesophageal Reflux Disease (GERD)',
  'Peptic Ulcer Disease', 'Irritable Bowel Syndrome', 'Migraine', 'Tension Headache',
  'Epilepsy', 'Stroke', 'Vertigo', "Parkinson's Disease", 'Peripheral Neuropathy',
  "Alzheimer's Disease", 'Bronchial Asthma', 'Chronic Obstructive Pulmonary Disease (COPD)',
  'Chronic Kidney Disease', 'Acute Kidney Injury', 'Renal Calculi',
  'Iron Deficiency Anemia', 'Vitamin B12 Deficiency Anemia', 'Thalassemia',
  'Osteoarthritis', 'Rheumatoid Arthritis', 'Low Back Pain', 'Sciatica',
  'Cervical Spondylosis', 'Osteoporosis', 'Gout', 'Anxiety Disorder', 'Depressive Disorder',
  'Atopic Dermatitis', 'Psoriasis', 'Fungal Skin Infection', 'Acne Vulgaris',
  'Vitamin D Deficiency', 'Vitamin B12 Deficiency', 'Iron Deficiency',
];

const MedicalCertificateModal = ({ onClose }) => {
  const { selectedPatientObj, selectedClinic, selectedAppointment, diagnosisCards } = useApp();
  const { user } = useAuth();

  const today = new Date().toISOString().split('T')[0];

  const [certType, setCertType] = useState('Sick Leave');
  const [diagnosis, setDiagnosis] = useState(diagnosisCards?.length ? [...diagnosisCards] : []);
  const [diagnosisInput, setDiagnosisInput] = useState('');
  const [fitForDuty, setFitForDuty] = useState(false); // Sick Leave default is unfit
  const [leaveDays, setLeaveDays] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState('');
  const [purpose, setPurpose] = useState(CERT_PREFILLS['Sick Leave'].purpose);
  const [remarks, setRemarks] = useState(CERT_PREFILLS['Sick Leave'].remarks);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const showLeaveFields = certType === 'Sick Leave' || certType === 'Disability';

  const patientName = `${selectedPatientObj?.name?.firstName || ''} ${selectedPatientObj?.name?.lastName || ''}`.trim();
  const phone = selectedPatientObj?.contact?.phoneNumber?.phoneNumber || 'N/A';

  const handleDiagnosisSelect = (selectedOptions) => {
    const newItems = selectedOptions.map(o => o.label);
    setDiagnosis(prev => [...prev, ...newItems]);
  };

  const handleDiagnosisKeyDown = (e) => {
    if (e.key === 'Enter' && diagnosisInput.trim()) {
      e.preventDefault();
      e.stopPropagation();
      setDiagnosis(prev => [...prev, diagnosisInput.trim()]);
      setDiagnosisInput('');
    }
  };

  const removeDiagnosis = (index) => {
    setDiagnosis(prev => prev.filter((_, i) => i !== index));
  };

  const buildBody = () => {
    const body = {
      patientId: selectedPatientObj._id,
      doctorId: user._id,
      clinicId: selectedClinic?._id,
      dateOfPublish: today,
      certificateType: certType,
      diagnosis: diagnosis.length ? diagnosis : undefined,
      fitForDuty,
      purpose: purpose.trim() || undefined,
      remarks: remarks.trim() || undefined,
    };
    if (selectedAppointment) body.appointmentId = selectedAppointment;
    if (showLeaveFields && leaveDays) body.leaveDays = Number(leaveDays);
    if (showLeaveFields && fromDate) body.fromDate = fromDate;
    if (showLeaveFields && toDate) body.toDate = toDate;
    Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
    return body;
  };

  const handleSubmit = async (sendPdf) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.post(
        '/mhr/medicalCertificateRecords/issue',
        buildBody(),
        {
          responseType: 'blob',
          params: { sendPdf: sendPdf ? 'true' : 'false' },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
      onClose(true);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mc-overlay" onClick={(e) => e.target === e.currentTarget && onClose(false)}>
      <div className="mc-modal">
        {/* Header */}
        <div className="mc-header">
          <h2 className="mc-title">Issue Medical Certificate</h2>
          <button className="mc-close-btn" onClick={() => onClose(false)}>✕</button>
        </div>

        {/* Patient info strip */}
        <div className="mc-patient-strip">
          <span><strong>Patient:</strong> {patientName}</span>
          <span><strong>Phone:</strong> {phone}</span>
        </div>

        {/* Scrollable form body */}
        <div className="mc-body">
          {/* Certificate Type */}
          <div className="mc-field">
            <label className="mc-label">Certificate Type</label>
            <div className="mc-type-pills">
              {CERT_TYPES.map(type => (
                <button
                  key={type}
                  className={`mc-type-pill ${certType === type ? 'mc-type-pill--active' : ''}`}
                  onClick={() => {
                    setCertType(type);
                    setPurpose(CERT_PREFILLS[type].purpose);
                    setRemarks(CERT_PREFILLS[type].remarks);
                    if (type !== 'Sick Leave' && type !== 'Disability') {
                      setLeaveDays(''); setFromDate(today); setToDate('');
                    }
                    if (type === 'Fitness') setFitForDuty(true);
                    else setFitForDuty(false);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Diagnosis */}
          <div className="mc-field">
            <label className="mc-label">Diagnosis</label>
            <Select
              isMulti
              placeholder="Search or type and press Enter..."
              value={[]}
              onChange={handleDiagnosisSelect}
              inputValue={diagnosisInput}
              onInputChange={(val, { action }) => {
                if (action === 'input-change') setDiagnosisInput(val);
              }}
              onKeyDown={handleDiagnosisKeyDown}
              options={DIAGNOSIS_OPTIONS.map(d => ({ label: d, value: d }))}
              classNamePrefix="mc-select"
              styles={{
                menu: base => ({ ...base, zIndex: 9999, maxHeight: 160 }),
                menuList: base => ({ ...base, maxHeight: 160 }),
              }}
            />
            {diagnosis.length > 0 && (
              <div className="mc-tags">
                {diagnosis.map((d, i) => (
                  <span key={i} className="mc-tag">
                    {d}
                    <span className="mc-tag-remove" onClick={() => removeDiagnosis(i)}>×</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Fit for Duty */}
          <div className="mc-field mc-field--row">
            <label className="mc-label">Fit for Duty</label>
            <div className="mc-toggle-row">
              <button
                className={`mc-toggle-btn ${fitForDuty ? '' : 'mc-toggle-btn--active mc-toggle-btn--unfit'}`}
                onClick={() => setFitForDuty(false)}
              >
                No (Unfit)
              </button>
              <button
                className={`mc-toggle-btn ${fitForDuty ? 'mc-toggle-btn--active mc-toggle-btn--fit' : ''}`}
                onClick={() => setFitForDuty(true)}
              >
                Yes (Fit)
              </button>
            </div>
          </div>

          {/* Leave fields — only for Sick Leave / Disability */}
          {showLeaveFields && (
            <div className="mc-leave-fields">
              <div className="mc-field">
                <label className="mc-label">Leave Days</label>
                <input
                  className="mc-input"
                  type="number"
                  min="1"
                  placeholder="e.g. 5"
                  value={leaveDays}
                  onChange={e => {
                    const val = e.target.value;
                    setLeaveDays(val);
                    if (fromDate && val) {
                      const end = new Date(fromDate);
                      end.setDate(end.getDate() + Number(val) - 1);
                      setToDate(end.toISOString().split('T')[0]);
                    }
                  }}
                />
              </div>
              <div className="mc-field">
                <label className="mc-label">From Date</label>
                <input
                  className="mc-input"
                  type="date"
                  value={fromDate}
                  onChange={e => {
                    setFromDate(e.target.value);
                    if (leaveDays) {
                      const end = new Date(e.target.value);
                      end.setDate(end.getDate() + Number(leaveDays) - 1);
                      setToDate(end.toISOString().split('T')[0]);
                    }
                  }}
                />
              </div>
              <div className="mc-field">
                <label className="mc-label">To Date</label>
                <input
                  className="mc-input"
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Purpose */}
          <div className="mc-field">
            <label className="mc-label">Purpose</label>
            <textarea
              className="mc-textarea"
              placeholder="e.g. Submission to employer for leave application"
              rows={2}
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
            />
          </div>

          {/* Remarks */}
          <div className="mc-field">
            <label className="mc-label">Remarks / Clinical Notes</label>
            <textarea
              className="mc-textarea"
              placeholder="e.g. Complete bed rest advised. Adequate hydration required."
              rows={3}
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
            />
          </div>

          {error && <p className="mc-error">{error}</p>}
        </div>

        {/* Footer */}
        <div className="mc-footer">
          <button className="mc-btn mc-btn--cancel" onClick={() => onClose(false)} disabled={isLoading}>
            Cancel
          </button>
          <button className="mc-btn mc-btn--issue-only" onClick={() => handleSubmit(false)} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Issue Only'}
          </button>
          <button className="mc-btn mc-btn--send-wa" onClick={() => handleSubmit(true)} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Issue + Send WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicalCertificateModal;
